/**
 * Shared campaign-order context (clientsInfo + enrichment objects).
 * Used by board / image / commission resolvers so they share one slug SSOT.
 */

import {
  getDestinationCampaignHotelByCode,
  resolveCampaignContract,
} from "../../data/destinationCampaignHotels";

export const BELVEDERE_CAMPAIGN_SLUG = "royal-belvedere-2026";
export const ROYAL_TULIP_CAMPAIGN_SLUG = "royal-tulip-tbilisi-2026";
export const ROYAL_ZANZIBAR_CAMPAIGN_SLUG = "royal-zanzibar-beach-resort-2026";

/** BE fallback when a campaign slug is missing from CAMPAIGN_HOTEL_DISPLAY_NAMES. */
const CAMPAIGN_HOTEL_NAME_PLACEHOLDERS = new Set([
  "מלון קמפיין",
  "campaign hotel",
]);

export function coerceRecord(value) {
  if (!value) return null;
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function coerceCampaignClientsInfo(order) {
  if (!order || typeof order !== "object") return {};
  return (
    coerceRecord(order.clientsinfo) ||
    coerceRecord(order.clientsInfo) ||
    coerceRecord(order.clients_info) ||
    {}
  );
}

export function resolveCampaignHotelContext(order) {
  if (!order || typeof order !== "object") return null;
  const admin = coerceRecord(order.admin_campaign_hotel);
  if (admin) return admin;
  const publicCtx = coerceRecord(order.campaign_hotel);
  return publicCtx;
}

function isCampaignHotelNamePlaceholder(value) {
  const t = String(value || "").trim();
  if (!t) return true;
  return CAMPAIGN_HOTEL_NAME_PLACEHOLDERS.has(t.toLowerCase()) || t === "מלון קמפיין";
}

/**
 * Display hotel name for campaign orders (details / cards / confirmation UI).
 * Prefers a real contract/registry name over the BE placeholder «מלון קמפיין».
 */
export function resolveCampaignOrderHotelDisplayName(order, fallback = "") {
  const ctx = resolveCampaignHotelContext(order) || {};
  const fromCtx = String(ctx.hotel_name || ctx.hotelName || "").trim();
  const slug = resolveCampaignSlugFromOrder(order);
  const contract = resolveCampaignContract(slug);
  const fromContract = String(
    contract?.hotelNameHe || contract?.hotelName || "",
  ).trim();
  const fromRegistry = String(
    getDestinationCampaignHotelByCode(slug)?.displayName || "",
  ).trim();
  if (fromCtx && !isCampaignHotelNamePlaceholder(fromCtx)) return fromCtx;
  return fromContract || fromRegistry || fromCtx || String(fallback || "").trim() || "";
}

export function resolveCampaignSelection(order) {
  const ci = coerceCampaignClientsInfo(order);
  const fromCi = coerceRecord(ci.campaign_selection);
  if (fromCi) return fromCi;
  const ctx = resolveCampaignHotelContext(order);
  return coerceRecord(ctx?.campaign_selection) || {};
}

export function resolveCampaignSelectedRoom(order) {
  const selection = resolveCampaignSelection(order);
  const top = coerceRecord(selection.selected_room);
  if (top?.board) return top;
  const nestedList = Array.isArray(top?.rooms) ? top.rooms : [];
  for (const row of nestedList) {
    const nested = coerceRecord(row);
    if (nested?.board) return nested;
  }
  return top;
}

export function resolveCampaignSlugFromOrder(order) {
  const ci = coerceCampaignClientsInfo(order);
  const selection = resolveCampaignSelection(order);
  const ctx = resolveCampaignHotelContext(order) || {};
  const raw = String(
    ctx.campaign ||
      ctx.campaign_slug ||
      ctx.hotel_code ||
      ci.campaign ||
      ci.campaign_slug ||
      ci.hotel_code ||
      selection.hotel_code ||
      "",
  ).trim();
  if (raw.startsWith("campaign-")) return raw.slice("campaign-".length);
  return raw;
}

export function isBelvedereCampaignSlug(slug) {
  return String(slug || "").trim() === BELVEDERE_CAMPAIGN_SLUG;
}

export function isRoyalTulipCampaignSlug(slug) {
  return String(slug || "").trim() === ROYAL_TULIP_CAMPAIGN_SLUG;
}

export function isRoyalZanzibarCampaignSlug(slug) {
  const value = String(slug || "").trim().toLowerCase().replace(/_/g, "-");
  return (
    value === ROYAL_ZANZIBAR_CAMPAIGN_SLUG ||
    value === "royal-zanzibar-2026" ||
    value === "royal-zanzibar"
  );
}

export function resolveCampaignAttributedAgentId(order) {
  if (!order || typeof order !== "object") return null;
  const ci = coerceCampaignClientsInfo(order);
  const ctx = resolveCampaignHotelContext(order) || {};
  const candidates = [
    order.agent_id,
    order.agentID,
    order.agentid,
    order.agentId,
    ctx.agent_id,
    ctx.referring_agent_id,
    ci.agent_id,
    ci.referring_agent_id,
  ];
  for (const value of candidates) {
    if (value == null || value === false || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
    const s = String(value).trim();
    if (s && s !== "0") return s;
  }
  return null;
}

export function isCampaignOrderAttributedToAgent(order) {
  if (resolveCampaignAttributedAgentId(order)) return true;
  const ci = coerceCampaignClientsInfo(order);
  const ctx = resolveCampaignHotelContext(order) || {};
  const entry = String(ci.entry_source || ctx.entry_source || "").trim().toLowerCase();
  if (entry === "agent_link" || entry === "agent_dashboard") return true;
  const label = String(ctx.booking_source_label || ci.booking_source_label || "").toLowerCase();
  if (label.includes("public client") || label.includes("agent_link") || label.includes("לינק")) {
    return true;
  }
  return false;
}
